import { useEffect } from "react";
import {
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { ResponsiveFormHost } from "../components/shared/ResponsiveFormHost";
import { ExpenseForm } from "../components/expenses/ExpenseForm";
import { ExpenseFormProvider } from "../context/ExpenseFormContext";
import { NotFoundPage } from "./NotFoundPage";
import { useEvents } from "../context/EventContext";
import { useIsMobile } from "../hooks/useIsMobile";

export function EditExpensePage() {
  const { eventId, expenseId } = useParams<{
    eventId: string;
    expenseId: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const {
    setCurrentEventId,
    currentEvent,
    isLoading,
    events,
    updateExpense,
  } = useEvents();

  useEffect(() => {
    if (eventId) {
      setCurrentEventId(eventId);
    }
    return () => setCurrentEventId(null);
  }, [eventId, setCurrentEventId]);

  const parentRoute = `/event/${eventId}/expenses`;
  const wizardRoot = `/event/${eventId}/expenses/${expenseId}/edit`;

  // Show 404 if event not found (after loading)
  if (!isLoading && eventId && !events.find((e) => e.id === eventId)) {
    return <NotFoundPage />;
  }

  if (isLoading || !currentEvent) {
    return null;
  }

  const expense = currentEvent.expenses.find((e) => e.id === expenseId);
  if (!expense) {
    return <NotFoundPage />;
  }

  const initialValues = {
    description: expense.description,
    amount: expense.amount,
    paidById: expense.paidById,
    participantIds: expense.participantIds,
  };

  // Mobile: nested routes drive the wizard.
  if (isMobile) {
    if (location.pathname === wizardRoot) {
      return <Navigate to="step-1" replace />;
    }

    return (
      <ExpenseFormProvider initialValues={initialValues}>
        <Outlet />
      </ExpenseFormProvider>
    );
  }

  // Desktop: render classic ExpenseForm inside Modal.
  const handleSubmit = (
    description: string,
    amount: number,
    paidById: string,
    participantIds: string[]
  ) => {
    if (!expenseId) return;
    updateExpense(expenseId, {
      description,
      amount,
      paidById,
      participantIds,
    });
    navigate(parentRoute);
  };

  return (
    <ResponsiveFormHost title="Editar gasto" parentRoute={parentRoute}>
      <ExpenseForm
        participants={currentEvent.participants}
        currency={currentEvent.currency}
        onSubmit={handleSubmit}
        onCancel={() => navigate(parentRoute)}
        initialValues={initialValues}
        submitLabel="Guardar cambios"
      />
    </ResponsiveFormHost>
  );
}
